import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Subject, takeUntil, take } from 'rxjs';
import { NavbarComponent } from '../navbar/navbar.component';
import { DashboardCardsComponent } from '../../../shared/cards/cards.component';
import { ModalType } from '../../enums/modal-type.enum';

import { SidebarService } from '../../../core/services/sidebar.service';
import { ModalService, ModalConfig } from '../../services/modal.service';

import { User, CreateUserDto } from '../../../features/users/models/user.model';
import { AppForm } from '../../../features/apps/models/app.model';
import { TeamForm, CreateTeamDto } from '../../../features/teams/models/team.model';

interface CreateAppData {
  appName: string;
  appDescription: string;
}

// Forms
import { AppFormComponent } from '../../../features/apps/components/app-form/app-form.component';
import { UserFormComponent } from '../../../features/users/components/user-form/user-form.component';
import { TeamFormComponent } from '../../../features/teams/components/team-form/team-form.component';

// Services
import { ApplicationsService } from '../../../features/apps/services/apps.service';
import { UsersService } from '../../../features/users/services/users.service';
import { GroupsService } from '../../../features/teams/services/teams.service';
import { ToasterService } from '../../../shared/toasts/services/toasts.service';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    CommonModule,
    SidebarComponent,
    NavbarComponent,
    RouterModule,
    MatSidenavModule,
    MatSidenav,
    DashboardCardsComponent,
    AppFormComponent,
    UserFormComponent,
    TeamFormComponent,
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  @ViewChild('drawer') drawer!: MatSidenav;
  @ViewChild(UserFormComponent) userFormRef?: UserFormComponent;
  @ViewChild(AppFormComponent) appFormRef?: AppFormComponent;
  @ViewChild(TeamFormComponent) teamFormRef?: TeamFormComponent;

  isSmallScreen = false;
  private destroy$ = new Subject<void>();
  private searchModalOpen = false;

  // UI state
  showUserForm = false;
  showAppForm = false;
  showTeamForm = false;

  // Edit data
  userEditData: User | null = null;
  appEditData: AppForm | null = null;
  teamEditData: TeamForm | null = null;

  private breakpointObserver = inject(BreakpointObserver);
  private sidebarService = inject(SidebarService);
  private modalService = inject(ModalService);
  private appsService = inject(ApplicationsService);
  private usersService = inject(UsersService);
  private groupsService = inject(GroupsService);
  private toaster = inject(ToasterService);

  ngOnInit(): void {
    this.breakpointObserver
      .observe(['(max-width: 1024px)'])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isSmallScreen = result.matches;
      });

    this.sidebarService.closeSidebar$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isSmallScreen && this.drawer) {
          this.drawer.close();
        }
      });
    this.modalService.closeModal$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.resetModals());

    this.modalService.openModal$
      .pipe(takeUntil(this.destroy$))
      .subscribe((config) => this.openModalByType(config));

    this.sidebarService.openModal$
      .pipe(takeUntil(this.destroy$))
      .subscribe((config) => this.openModalByType(config));
  }

  private openModalByType(config: ModalConfig) {
    this.resetModals();

    switch (config.type) {
      case ModalType.USER:
        this.showUserForm = true;
        this.userEditData = config.data ?? null;
        break;
      case ModalType.TEAM:
        this.showTeamForm = true;
        this.teamEditData = config.data ?? null;
        break;
      case ModalType.APP:
        this.showAppForm = true;
        this.appEditData = config.data ?? null;
        break;
    }
  }

  private resetModals() {
    this.showUserForm = false;
    this.showAppForm = false;
    this.showTeamForm = false;
  }

  public closeModalSuccess() {
    this.resetModals();
    this.modalService.close();
    this.sidebarService.notifyModalSuccess();
  }

  // ========================
  // SUBMITS
  // ========================

  onAppSubmit(appData: Partial<AppForm>) {
    const createData: CreateAppData = {
      appName: appData.appName ?? '',
      appDescription: appData.appDescription ?? '',
    };

    this.appsService
      .createAppAndReload(createData)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toaster.success('Creación exitosa', 'La aplicación se ha creado correctamente');
          this.closeModalSuccess();
        },
        error: () => {
          if (this.appFormRef) this.appFormRef.isLoading = false;
          this.toaster.error('Error', 'No se pudo crear la aplicación');
        },
      });
  }

  onAppEditSubmit(appData: Partial<AppForm> | FormData) {
    if (!this.appEditData?.appId) return;

    this.appsService.updateApp(this.appEditData.appId, appData).subscribe({
      next: () => {
        this.toaster.success('Actualización exitosa', 'La aplicación se ha actualizado correctamente');
        this.closeModalSuccess();
      },
      error: () => {
        if (this.appFormRef) this.appFormRef.isLoading = false;
        this.toaster.error('Error', 'No se pudo actualizar la aplicación');
      },
    });
  }

  onUserSubmit(userData: CreateUserDto) {
    this.usersService.createUser(userData).subscribe({
      next: () => {
        this.toaster.success('Creación exitosa', 'El usuario se ha creado correctamente');
        this.closeModalSuccess();
      },
      error: () => {
        if (this.userFormRef) this.userFormRef.isLoading = false;
        this.toaster.error('Error', 'No se pudo crear el usuario');
      },
    });
  }

  onUserEditSubmit(userData: Partial<User>) {
    if (!this.userEditData?.userId) return;

    const transformedData: Partial<User> = {
      ...userData,
      emails: userData.email ? [{ email: userData.email }] : undefined,
      phones: userData.phone
        ? [
            {
              isoCountry: userData.isoCountry ?? 'CO',
              nationalNumber: String(userData.phone),
              isPrimary: true,
            },
          ]
        : undefined,
    };

    delete transformedData.email;
    delete transformedData.phone;

    this.usersService.updateUser(this.userEditData.userId, transformedData).subscribe({
      next: () => {
        this.toaster.success('Actualización exitosa', 'El usuario fue actualizado correctamente');
        this.closeModalSuccess();
      },
      error: () => {
        if (this.userFormRef) this.userFormRef.isLoading = false;
        this.toaster.error('Error', 'No se pudo actualizar el usuario');
      },
    });
  }

  onTeamSubmit(teamData: CreateTeamDto) {
    const request = this.teamEditData?.groupId
      ? this.groupsService.updateGroup(
          this.teamEditData.groupId,
          teamData as TeamForm,
        )
      : this.groupsService.createTeam(teamData);

    request.subscribe({
      next: () => {
        this.toaster.success(
          this.teamEditData ? 'Actualización exitosa' : 'Creación exitosa',
          this.teamEditData ? 'El grupo fue actualizado correctamente' : 'El grupo fue creado correctamente',
        );
        this.closeModalSuccess();
      },
      error: () => {
        if (this.teamFormRef) this.teamFormRef.isLoading = false;
        this.toaster.error(
          this.teamEditData ? 'Error' : 'Error',
          this.teamEditData ? 'No se pudo actualizar el grupo' : 'No se pudo crear el grupo',
        );
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
